import React, { Fragment, useContext, useMemo } from 'react';
import { Accordion } from '../../../components/containers/Accordion';
import { AccordionTitle } from '../../../components/containers/Accordion/AccordionTitle';
import { SocialProfile } from '../../../components/socialProfiles/SocialProfile';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { AccordionContent } from '../../../components/containers/Accordion/AccordionContent';
import {
  AccordionErrorMessageType,
  AccordionProviderType
} from '../../../components/containers/Accordion/AccordionProvider';
import { ScheduleBox } from '../Schedule/ScheduleBox';
import { UseFormReturn } from 'react-hook-form';
import { ActiveCustomizeIndexContextProvider } from '../states/ActiveCustomizeIndexProvider';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { PostStatusSelector } from '../PostStatusSelector';
import { ReadonlyGroupPostAttributesType } from '../../../models/post-editor';
import { PostEditorErrorKeys } from '../../../models/validator-props';
import { DisabledCustomizedPostView } from '../DisabledCustomizedPost/DisabledCustomizedPost.view';
import { IconsEnum } from '../../../constants/icons';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../constants/form';
import { Tooltip } from '../../../components/actions/Tooltip';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type CustomizeSchedulePerProfileViewProps = {
  //
  selectedProfiles: SocialProfileWithBoardsAndMentionType[];
  formInstance: UseFormReturn;
  activeIndex?: AccordionProviderType['activeIndex'];
  onTabChange?: AccordionProviderType['onTabChange'];
  readOnlyProfiles: SocialProfileWithBoardsAndMentionType[];
  readOnlyPosts: ReadonlyGroupPostAttributesType[];
  showPostStatusSelector: boolean;
  postId?: string;
};

const POSSIBLE_WARNING_ERRORS_SCHEDULE: PostEditorErrorKeys[] = ['checkExpiryDate'];
const POSSIBLE_ERRORS_SCHEDULE: PostEditorErrorKeys[] = [
  ...POSSIBLE_WARNING_ERRORS_SCHEDULE,
  'checkDateAndTime',
  'checkExpiryCount',
  'checkQueuePosition',
  'expireCountRequired',
  'expireDateRequired',
  'pinterestDuplicatesCheck',
  'preventPostNowForDisconnected',
  'preventTwDuplicate',
  'scheduleTextOrMediaVariations'
];
export const CustomizeSchedulePerProfileView = ({
  activeIndex,
  onTabChange,
  selectedProfiles,
  formInstance,
  readOnlyProfiles,
  readOnlyPosts,
  showPostStatusSelector,
  postId
}: CustomizeSchedulePerProfileViewProps): JSX.Element => {
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { state } = useContext(PostEditorContext);
  const { postGroupIndex } = tabsState;

  const titleAccordionPrefix = 'schedule_title_';
  const errors = JSON.stringify(
    selectedProfiles.flatMap((profile, index) => {
      const errorId = `${titleAccordionPrefix}${profile.id}_${index}`;
      const isCollapsed = activeIndex !== index;
      const hasError = hasSeverity(
        state.postGroups[postGroupIndex].errors,
        ErrorMessageSeverity.error,
        profile.id,
        undefined,
        POSSIBLE_ERRORS_SCHEDULE
      );
      const hasWarning = hasSeverity(
        state.postGroups[postGroupIndex].errors,
        ErrorMessageSeverity.warning,
        profile.id,
        undefined,
        POSSIBLE_WARNING_ERRORS_SCHEDULE
      );
      return isCollapsed && (hasError || hasWarning)
        ? { id: errorId, severity: hasError ? ErrorMessageSeverity.error : ErrorMessageSeverity.warning }
        : [];
    })
  );

  const errorsArr: AccordionErrorMessageType[] = useMemo(() => JSON.parse(errors) || [], [errors]);

  const screenSize = useBreakpoints();
  const isMobile = screenSize === 'small' || screenSize === 'tabletportrait';

  return (
    <>
      <Accordion
        collapseAll={true}
        {...(errorsArr.length ? { error: errorsArr } : {})}
        {...(typeof activeIndex === 'number' ? { activeIndex } : {})}
        {...(onTabChange ? { onTabChange } : {})}
        segmentedTitle>
        {selectedProfiles.map((profile, index) => {
          const errorsForThisProfile = errorsArr.filter(e => e.id === `${titleAccordionPrefix}${profile.id}_${index}`);
          return (
            <Fragment key={`${profile.id}_${index}_${errorsForThisProfile.join('_')}`}>
              <AccordionTitle id={`${titleAccordionPrefix}${profile.id}_${index}`} tabindex={index}>
                <div className={'grid-x align-justify'}>
                  <div className={`cell ${isMobile ? '' : 'auto'}`}>
                    <SocialProfile
                      socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
                      size={'small'}
                      label
                      image={profile.photoUrl}
                      disconnected={!profile.connected}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <div className={'grid-x align-left align-middle'}>
                      {errorsForThisProfile.length > 0 ? (
                        <div className={'cell shrink'}>
                          <Tooltip
                            icon={
                              !!errorsForThisProfile.find(e => e.severity === ErrorMessageSeverity.error)
                                ? 'alert'
                                : 'warning'
                            }
                            target={`#tooltipTargetMediaBlockWarning_${index}_${postGroupIndex}`}
                            position={'right'}
                            pointer={true}
                            type={'inlined'}
                            autoZIndex={false}
                            style={{ zIndex: scssVars['z-index-overmodal'] }}>
                            <>{PostEditorValidationMessages.expandAccordionTooltip}</>
                          </Tooltip>
                        </div>
                      ) : null}
                      {showPostStatusSelector ? (
                        <div className={'cell shrink'}>
                          <ActiveCustomizeIndexContextProvider index={index}>
                            <PostStatusSelector
                              key={`${profile.id}_${index}`}
                              formInstance={formInstance}
                              customizeType={'schedule'}
                              transparent
                            />
                          </ActiveCustomizeIndexContextProvider>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {/*{showPostStatusSelector ? (*/}
                  {/*  <div className={'cell shrink'}>*/}
                  {/*    <div className={'grid-x align-left align-middle'}>*/}
                  {/*      {errorsForThisProfile.length > 0 ? (*/}
                  {/*        <div className={'cell shrink'}>*/}
                  {/*          <Tooltip*/}
                  {/*            icon={*/}
                  {/*              !!errorsForThisProfile.find(e => e.severity === ErrorMessageSeverity.error)*/}
                  {/*                ? 'alert'*/}
                  {/*                : 'warning'*/}
                  {/*            }*/}
                  {/*            target={`#tooltipTargetScheduleWarning_${index}_${postGroupIndex}`}*/}
                  {/*            position={'right'}*/}
                  {/*            pointer={true}*/}
                  {/*            type={'inlined'}*/}
                  {/*            autoZIndex={false}*/}
                  {/*            style={{ zIndex: scssVars['z-index-overmodal'] }}>*/}
                  {/*            <>{PostEditorValidationMessages.expandAccordionTooltip}</>*/}
                  {/*          </Tooltip>*/}
                  {/*        </div>*/}
                  {/*      ) : null}*/}
                  {/*      <div className={'cell shrink'}>*/}
                  {/*        <ActiveCustomizeIndexContextProvider index={index}>*/}
                  {/*          <PostStatusSelector formInstance={formInstance} customizeType={'schedule'} transparent />*/}
                  {/*        </ActiveCustomizeIndexContextProvider>*/}
                  {/*      </div>*/}
                  {/*    </div>*/}
                  {/*  </div>*/}
                  {/*) : null}*/}
                </div>
              </AccordionTitle>
              <AccordionContent
                id={`schedule_content_${profile.id}_${index}`}
                titleId={`${titleAccordionPrefix}${profile.id}_${index}`}
                borderless>
                <ActiveCustomizeIndexContextProvider index={typeof activeIndex === 'number' ? activeIndex : undefined}>
                  <ScheduleBox formInstance={formInstance} postId={postId} />
                </ActiveCustomizeIndexContextProvider>
              </AccordionContent>
            </Fragment>
          );
        })}
      </Accordion>
      {readOnlyPosts.map((post, index) => {
        const readonlyProfile = readOnlyProfiles.find(p => p.id === post.customizedProfileId && post.active);
        return readonlyProfile ? (
          <DisabledCustomizedPostView
            key={`${post.customizedProfileId}_${selectedProfiles.length + index}`}
            isFirst={!selectedProfiles.length && index === 0}
            accordionIcon={IconsEnum.arrowDown}
            tabindex={selectedProfiles.length + index}>
            <div className={'grid-x align-justify'}>
              <div className={'cell shrink'}>
                <SocialProfile
                  disabled={true}
                  socialProfile={profilesHelpers.remapSocialProfile(readonlyProfile, false, false, false)}
                  size={'small'}
                  label
                  image={readonlyProfile.photoUrl}
                  disconnected={!readonlyProfile.connected}
                />
              </div>
            </div>
          </DisabledCustomizedPostView>
        ) : null;
      })}
    </>
  );
};
